import type { Locale } from "@/types"

/**
 * Supported UI locales for the language switcher (OKS-96).
 *
 * Mirrors `User::LOCALES` on the Rails side
 * (`%w[en fr].freeze`). Adding a third locale = append the
 * `{ value, nativeLabel }` entry below + add to `User::LOCALES` +
 * ship `config/locales/{backend,frontend}/<namespace>.<lang>.yml`
 * dictionaries. The `<LanguageSection>` component renders this
 * array directly, so no edits to the switcher itself are needed
 * (PRD §3 criterion 4 — future-proofed).
 *
 * `nativeLabel` is the locale's name **in its own language**:
 * - English → "English" (never "Anglais")
 * - Français → "Français" (never "French")
 *
 * Native labels live HERE rather than in the i18next dictionary
 * so they survive any future translation sweep — a user looking
 * for "Français" in an English-rendered UI must always find
 * "Français", not "French" (PRD §3 / UX Spec §8). The YAML guard
 * comment in `config/locales/frontend/settings.{en,fr}.yml`
 * documents the inverse rule.
 */
export const SUPPORTED_LOCALES = [
  { value: "en", nativeLabel: "English" },
  { value: "fr", nativeLabel: "Français" },
] as const satisfies readonly { value: Locale; nativeLabel: string }[]

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/**
 * Lookup helper — returns the native label for a `Locale`, or the
 * raw locale string as a defensive fallback if a value somehow
 * escapes `User::LOCALES` (shouldn't happen — model inclusion
 * validation guards the column — but the Select would otherwise
 * render as blank).
 */
export function nativeLabelFor(locale: string): string {
  const match = SUPPORTED_LOCALES.find((entry) => entry.value === locale)
  return match?.nativeLabel ?? locale
}

/**
 * OKS-1032 — Narrow an i18next language string to an app `Locale`.
 *
 * Exactly `"en"` → `"en"`; anything else (including `undefined`, `""`,
 * `"en-US"`, `"de"`) → `"fr"`, the app default. In practice i18next only ever
 * holds `"en"` or `"fr"` here: `initI18n` passes
 * `supportedLngs: AVAILABLE_LOCALES`, which maps `"en-US"` to `"en"` and
 * unknown languages to the `"fr"` fallback before any component reads them.
 *
 * ⚠️ NOT for the sites that narrow with `startsWith("fr")` (candidates
 * filters/rows, creator profile card): their default is `"en"`, and routing
 * them through this helper would flip their fallback.
 *
 * Lifted from the private copy in `pages/Brand/Rewards/Redemptions/Index.tsx`
 * (OKS-1025). Pure — no i18next import, safe in SSR and leaf components.
 *
 * @param language  `useTranslation().i18n.language`.
 * @returns `"en"` for exactly `"en"`, otherwise `"fr"`.
 */
export function toAppLocale(language: string | undefined): Locale {
  return language === "en" ? "en" : "fr"
}
